import { createContext, FunctionComponent } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';

import { usePlayer } from './PlayerProvider';

export type CastingType = 'chromecast' | 'airPlay';

interface CastContextProps {
  isAvailable: boolean;
  isActive: boolean;
  deviceName: string;
  castingType?: CastingType;
}

const CastContext = createContext<CastContextProps>(undefined!);

export interface CastProviderProps {
  state?: jwplayer.CastParam;
}

const isAirplaySupported = 'WebKitPlaybackTargetAvailabilityEvent' in window;

export const CastProvider: FunctionComponent<CastProviderProps> = ({
  state: stateParam,
  children,
}) => {
  const player = usePlayer();
  const [state, setState] = useState<CastContextProps>({
    isAvailable: false,
    isActive: false,
    deviceName: '',
  });

  const isChromecastActive =
    state.isActive && state.castingType === 'chromecast';

  useEffect(() => {
    if (isChromecastActive) {
      // destroy pause plugin to remove existing listeners when chromecast starts
      player.getPlugin('PausePlugin')?.destroy();
    }
  }, [player, isChromecastActive]);

  useEffect(() => {
    if (!stateParam) {
      return;
    }

    let castingType: CastingType | undefined;

    if (stateParam.available) {
      castingType = isAirplaySupported ? 'airPlay' : 'chromecast';
    }

    setState({
      isAvailable: stateParam.available,
      isActive: stateParam.active,
      deviceName: stateParam.deviceName,
      castingType,
    });
  }, [stateParam]);

  return <CastContext.Provider value={state}>{children}</CastContext.Provider>;
};

export const useCast = () => {
  const cast = useContext(CastContext);

  if (process.env.NODE_ENV !== 'production' && !cast) {
    console.warn(
      'Cast context is undefined, please verify you are calling useCast() as child of a <CastProvider> component.'
    );
  }

  return cast;
};
